import type { FileContents, FileDiffMetadata, SupportedLanguages } from '@pierre/diffs';

type PierreFileContentsRole = 'old' | 'new';

const DJB2_INITIAL_HASH = 5381;
const DJB2_SECONDARY_HASH = 52711;
const DJB2_MULTIPLIER = 33;

function hashPierreCacheMaterial(value: string): string {
  let hashA = DJB2_INITIAL_HASH;
  let hashB = DJB2_SECONDARY_HASH;

  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    hashA = Math.imul(hashA, DJB2_MULTIPLIER) ^ code;
    hashB = Math.imul(hashB, DJB2_MULTIPLIER) ^ (code + index);
  }

  return `${value.length.toString(36)}:${(hashA >>> 0).toString(36)}:${(hashB >>> 0).toString(36)}`;
}

/** Share the displayed source version with session comment snapshots. */
export function fingerprintFileContents(contents: string): string {
  return hashPierreCacheMaterial(contents);
}

/**
 * Pierre 1.4 no longer derives highlight-cache keys. Worker-pool caching
 * requires an explicit `cacheKey` that changes whenever contents change.
 */
export function createPierreFileContents({
  name,
  contents,
  role,
  lang,
}: {
  name: string;
  contents: string;
  role: PierreFileContentsRole;
  lang?: SupportedLanguages;
}): FileContents {
  return {
    name,
    contents,
    ...(lang === undefined ? {} : { lang }),
    cacheKey: `${role}:${name}:${fingerprintFileContents(contents)}`,
  };
}

/**
 * FileDiff is the common stripped-payload path. Older materialized metadata
 * has no `cacheKey`, so the worker pool would still skip highlight caching
 * until rematerialization. Derive a key from the lines Pierre highlights.
 */
export function ensurePierreFileDiffCacheKey(fileDiff: FileDiffMetadata): FileDiffMetadata {
  if (fileDiff.cacheKey) {
    return fileDiff;
  }

  const deletionLines = fileDiff.deletionLines ?? [];
  const additionLines = fileDiff.additionLines ?? [];

  return {
    ...fileDiff,
    cacheKey: `fd:${fileDiff.type}:${fileDiff.name}:${fileDiff.prevName ?? ''}:${hashPierreCacheMaterial(
      deletionLines.join('\n'),
    )}:${hashPierreCacheMaterial(additionLines.join('\n'))}`,
  };
}
